// Which bank a statement PDF is from, read from its header: the bank's name or the IFSC printed at the top of
// page 1. Used when no parser reads the file, so the "can't read <bank> yet" screen and the anonymous
// hit/miss count can name the bank. Only a short code leaves this file (never text from the statement).
import type { PdfItem } from "../pdf-items"

/** Code (a short [a-z0-9] id, safe as an analytics value), display name, name pattern, IFSC prefix. */
const BANKS: readonly [code: string, name: string, pattern: RegExp, ifsc: string][] = [
  ["sbi", "SBI", /\bstate bank of india\b|\bsbi\b/i, "SBIN"],
  ["hdfc", "HDFC Bank", /\bhdfc\b/i, "HDFC"],
  ["icici", "ICICI Bank", /\bicici\b/i, "ICIC"],
  ["axis", "Axis Bank", /\baxis bank\b/i, "UTIB"],
  ["kotak", "Kotak", /\bkotak\b/i, "KKBK"],
  ["indusind", "IndusInd Bank", /\bindusind\b/i, "INDB"],
  ["yes", "Yes Bank", /\byes bank\b/i, "YESB"],
  ["idfc", "IDFC FIRST Bank", /\bidfc\b/i, "IDFB"],
  ["bob", "Bank of Baroda", /\bbank of baroda\b/i, "BARB"],
  ["pnb", "Punjab National Bank", /\bpunjab national bank\b|\bpnb\b/i, "PUNB"],
  ["canara", "Canara Bank", /\bcanara bank\b/i, "CNRB"],
  ["union", "Union Bank of India", /\bunion bank of india\b/i, "UBIN"],
  ["boi", "Bank of India", /\bbank of india\b/i, "BKID"],
  ["indian", "Indian Bank", /\bindian bank\b/i, "IDIB"],
  ["central", "Central Bank of India", /\bcentral bank of india\b/i, "CBIN"],
  ["iob", "Indian Overseas Bank", /\bindian overseas bank\b/i, "IOBA"],
  ["uco", "UCO Bank", /\buco bank\b/i, "UCBA"],
  ["bom", "Bank of Maharashtra", /\bbank of maharashtra\b/i, "MAHB"],
  ["psb", "Punjab & Sind Bank", /\bpunjab (?:&|and) sind\b/i, "PSIB"],
  ["federal", "Federal Bank", /\bfederal bank\b/i, "FDRL"],
  ["sib", "South Indian Bank", /\bsouth indian bank\b/i, "SIBL"],
  ["karnataka", "Karnataka Bank", /\bkarnataka bank\b/i, "KARB"],
  ["kvb", "Karur Vysya Bank", /\bkarur vysya\b/i, "KVBL"],
  ["cub", "City Union Bank", /\bcity union bank\b/i, "CIUB"],
  ["rbl", "RBL Bank", /\brbl bank\b/i, "RATN"],
  ["bandhan", "Bandhan Bank", /\bbandhan bank\b/i, "BDBL"],
  ["au", "AU Small Finance Bank", /\bau small finance\b/i, "AUBL"],
  ["equitas", "Equitas Small Finance Bank", /\bequitas\b/i, "ESFB"],
  ["ujjivan", "Ujjivan Small Finance Bank", /\bujjivan\b/i, "UJVN"],
  ["jana", "Jana Small Finance Bank", /\bjana small finance\b/i, "JSFB"],
  ["idbi", "IDBI Bank", /\bidbi\b/i, "IBKL"],
  ["dbs", "DBS Bank", /\bdbs bank\b/i, "DBSS"],
  ["sc", "Standard Chartered", /\bstandard chartered\b/i, "SCBL"],
  ["hsbc", "HSBC", /\bhsbc\b/i, "HSBC"],
  ["citi", "Citibank", /\bciti ?bank\b/i, "CITI"],
  ["amex", "Amex", /\bamerican express\b/i, ""],
  ["airtel", "Airtel Payments Bank", /\bairtel payments bank\b/i, "AIRP"],
  ["ippb", "India Post Payments Bank", /\bindia post payments bank\b/i, "IPOS"],
  ["paytm", "Paytm Payments Bank", /\bpaytm payments bank\b/i, "PYTM"],
  ["fino", "Fino Payments Bank", /\bfino payments bank\b/i, "FINO"],
  ["saraswat", "Saraswat Bank", /\bsaraswat\b/i, "SRCB"],
  ["cosmos", "Cosmos Bank", /\bcosmos (?:co-operative )?bank\b/i, "COSB"],
  ["dhanlaxmi", "Dhanlaxmi Bank", /\bdhanlaxmi\b/i, "DLXB"],
  ["tmb", "Tamilnad Mercantile Bank", /\btamilnad mercantile\b/i, "TMBL"],
  ["csb", "CSB Bank", /\bcsb bank\b|\bcatholic syrian bank\b/i, "CSBK"],
  ["jkbank", "J&K Bank", /\bj ?& ?k bank\b|\bjammu (?:&|and) kashmir bank\b/i, "JAKA"],
  ["suryoday", "Suryoday Small Finance Bank", /\bsuryoday\b/i, "SURY"],
  ["esaf", "ESAF Small Finance Bank", /\besaf\b/i, "ESMF"],
  ["utkarsh", "Utkarsh Small Finance Bank", /\butkarsh\b/i, "UTKS"],
]

const BY_IFSC = new Map(BANKS.filter((b) => b[3]).map((b) => [b[3], b]))
const IFSC = /\b([A-Z]{4})0[A-Z0-9]{6}\b/g

export type StatementBank = { code: string; name: string }

/** A bank code's display name ("kotak" → "Kotak"); null for a code not in the list. */
export function bankNameOf(code: string): string | null {
  return BANKS.find((b) => b[0] === code)?.[1] ?? null
}

/**
 * The bank a statement is from: page 1 read top to bottom, and the bank whose name or IFSC comes first (the
 * header, before any payee bank in the rows). Null when page 1 names no bank this list knows.
 */
export function statementBank(items: readonly PdfItem[]): StatementBank | null {
  return bankInText(
    items
      .filter((i) => i.page === 1)
      .sort((a, b) => b.y - a.y || a.x - b.x)
      .map((i) => i.str)
      .join(" "),
  )
}

/** Every bank this list knows, as [code, display name] (for a "which bank" picker). */
export const BANK_OPTIONS: readonly (readonly [string, string])[] = BANKS.map((b) => [b[0], b[1]])

/**
 * The bank named first in a piece of text: a statement header, or a file name ("HDFC_Statement_Oct23.pdf"; a
 * password PDF can't be read for its header, so the file name is all there is before it is opened).
 */
export function bankInText(text: string): StatementBank | null {
  let best: { at: number; len: number; bank: (typeof BANKS)[number] } | null = null
  const consider = (at: number, len: number, bank: (typeof BANKS)[number]) => {
    if (!best || at < best.at || (at === best.at && len > best.len)) best = { at, len, bank }
  }
  for (const bank of BANKS) {
    const m = bank[2].exec(text)
    if (m) consider(m.index, m[0].length, bank)
  }
  for (const m of text.matchAll(IFSC)) {
    const bank = BY_IFSC.get(m[1])
    if (bank) {
      consider(m.index, m[0].length, bank)
      break
    }
  }
  const found = best as { bank: (typeof BANKS)[number] } | null
  return found ? { code: found.bank[0], name: found.bank[1] } : null
}
